import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { TextArea } from '@shared/components/ui/textarea';
import { Ban } from 'lucide-react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface CancelReceiptModalRef {
  open: (receiptCode: string, onConfirm: (cancelReason: string) => Promise<void>) => void;
}

export const CancelReceiptModal = forwardRef<CancelReceiptModalRef>((_, ref) => {
  const { t } = useTranslation('merchandiseV3');
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmCallbackRef = useRef<((cancelReason: string) => Promise<void>) | null>(null);
  const [receiptCode, setReceiptCode] = useState<string>('');
  const [cancelReason, setCancelReason] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useImperativeHandle(ref, () => ({
    open: (code: string, onConfirm: (reason: string) => Promise<void>) => {
      setReceiptCode(code);
      setCancelReason('');
      setValidationError(null);
      setIsProcessing(false);
      confirmCallbackRef.current = onConfirm;
      modalRef.current?.openModal();
    },
  }));

  const handleClose = () => {
    if (isProcessing) return;
    modalRef.current?.closeModal();
  };

  const handleConfirm = async () => {
    const trimmedReason = cancelReason.trim();
    if (!trimmedReason) {
      setValidationError(
        t('receive.cancel.modal.reason_required', 'Debes ingresar una razón para cancelar la recepción'),
      );
      return;
    }

    if (!confirmCallbackRef.current) return;

    setValidationError(null);
    setIsProcessing(true);
    try {
      await confirmCallbackRef.current(trimmedReason);
      modalRef.current?.closeModal();
    } catch (error) {
      console.error('Error al cancelar recepción:', error);
      modalRef.current?.closeModal();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={t('receive.cancel.modal.title', 'Cancelar Recepción')}
      type="error"
      width="lg"
      onCloseModal={() => {
        setIsProcessing(false);
        setValidationError(null);
      }}
    >
      <div className="space-y-5">
        <div className="rounded-lg border border-card-border bg-card-bg p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-bg-error">
              <Ban className="w-5 h-5 text-error" />
            </div>
            <div>
              <p className="text-sm text-subtext">{t('receive.cancel.modal.receipt_label', 'Recepción')}</p>
              <p className="text-base font-semibold text-title">{receiptCode}</p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-text">
            {t('receive.cancel.modal.reason_label', 'Razón de cancelación')}
          </p>
          <TextArea
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
            rows={4}
            maxLength={500}
            placeholder={t('receive.cancel.modal.reason_placeholder', 'Ej: Producto dañado')}
            data-testid="cancel-receipt-reason-textarea"
            disabled={isProcessing}
          />
          {validationError && <p className="text-xs text-error">{validationError}</p>}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-card-border">
          <Button
            variant="outline"
            size="lg"
            onClick={handleClose}
            disabled={isProcessing}
            data-testid="cancel-receipt-modal-close-button"
          >
            {t('receive.cancel.modal.close', 'Volver')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleConfirm}
            disabled={isProcessing}
            data-testid="cancel-receipt-modal-confirm-button"
          >
            {isProcessing
              ? t('receive.cancel.modal.processing', 'Cancelando...')
              : t('receive.cancel.modal.confirm', 'Confirmar Cancelación')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

CancelReceiptModal.displayName = 'CancelReceiptModal';
